// Image strip. Desktop: the section pins and page scroll walks the strip to its last image, then the page
// moves on. Phones and reduced motion: native scroll with snap. Arrows, drag and counter work in both.
const PIN_QUERY = '(min-width: 990px) and (prefers-reduced-motion: no-preference)';

class QoSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.qo-slider__track');
    this.slides = [...this.track.children];
    this.arrows = [...this.querySelectorAll('[data-step]')];
    this.current = this.querySelector('[data-current]');
    this.querySelector('[data-total]').textContent = this.pad(this.slides.length);
    this.section = this.closest('.qo-gallery');
    this.pinQuery = window.matchMedia(PIN_QUERY);

    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    };
    this.onLayout = () => this.layout();

    this.arrows.forEach((arrow) => arrow.addEventListener('click', () => this.go(Number(arrow.dataset.step))));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', (event) => this.dragStart(event));
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
    this.pinQuery.addEventListener('change', this.onLayout);
    this.resizer = new ResizeObserver(this.onLayout);
    this.resizer.observe(this.track);
    this.layout();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('scroll', this.onScroll);
    this.pinQuery?.removeEventListener('change', this.onLayout);
    this.resizer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  layout() {
    this.pinned = this.pinQuery.matches && Boolean(this.section);
    this.section?.classList.toggle('is-pinned', this.pinned);
    this.distance = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    // The pin starts once the top padding has scrolled past.
    this.padTop = this.section ? parseFloat(getComputedStyle(this.section).paddingTop) || 0 : 0;
    // The section grows by the strip's overflow, so scrolling that far down moves the strip that far left.
    this.section?.style.setProperty('--qo-pin-distance', `${this.distance}px`);
    if (!this.pinned) this.track.style.transform = '';
    this.update();
  }

  position() {
    if (!this.pinned) return this.track.scrollLeft;
    return Math.min(this.distance, Math.max(0, -this.section.getBoundingClientRect().top - this.padTop));
  }

  setPosition(x, smooth) {
    const behavior = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto';
    if (this.pinned) {
      window.scrollTo({ top: window.scrollY + this.section.getBoundingClientRect().top + this.padTop + x, behavior });
    } else {
      this.track.scrollTo({ left: x, behavior });
    }
  }

  // Mouse drag; touch and trackpads already scroll natively.
  dragStart(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const startX = event.clientX;
    const startPos = this.position();
    this.track.setPointerCapture(event.pointerId);
    this.track.classList.add('is-dragging');

    const move = (e) => this.setPosition(startPos - (e.clientX - startX), false);
    const end = (e) => {
      this.track.removeEventListener('pointermove', move);
      this.track.removeEventListener('pointerup', end);
      this.track.removeEventListener('pointercancel', end);
      // Land on the next slide in the drag direction once the drag is more than a nudge.
      const dx = e.clientX - startX;
      const step = Math.abs(dx) > 40 ? (dx < 0 ? 1 : -1) : 0;
      const from = this.slides.findIndex((slide) => slide.offsetLeft >= startPos - 1);
      this.goTo(Math.max(0, from) + step);
      // Snap comes back only after the glide, or it would jump mid-way.
      const release = () => this.track.classList.remove('is-dragging');
      (this.pinned ? window : this.track).addEventListener('scrollend', release, { once: true });
      setTimeout(release, 900);
    };
    this.track.addEventListener('pointermove', move);
    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);
  }

  pad(n) {
    return String(n).padStart(2, '0');
  }

  index(x = this.position()) {
    let nearest = 0;
    this.slides.forEach((slide, i) => {
      if (Math.abs(slide.offsetLeft - x) < Math.abs(this.slides[nearest].offsetLeft - x)) nearest = i;
    });
    return nearest;
  }

  go(step) {
    this.goTo(this.index() + step);
  }

  goTo(i) {
    const target = this.slides[Math.min(this.slides.length - 1, Math.max(0, i))];
    this.setPosition(Math.min(target.offsetLeft, this.distance), true);
  }

  update() {
    const x = this.position();
    if (this.pinned) this.track.style.transform = `translate3d(${-x}px, 0, 0)`;
    const atEnd = x >= this.distance - 1;
    // Several slides fit on screen, so the end of the strip counts as the last slide.
    this.current.textContent = this.pad(atEnd ? this.slides.length : this.index(x) + 1);
    this.arrows[0].disabled = x <= 0;
    this.arrows[1].disabled = atEnd;
  }
}

if (!customElements.get('qo-slider')) customElements.define('qo-slider', QoSlider);
